import { useState, useEffect, useRef, useMemo } from "react";
import {
  ChatContainer as ChatContainerLibrary,
  MessageList,
  Message,
  Avatar,
} from "@chatscope/chat-ui-kit-react";
import {
  CAMPAIGN_AGENT_FOLLOW_UP_REPLIES,
  DRAWER_CAMPAIGN,
  FONT_FAMILY,
  MAP_CHAT_FROM,
  OWNER_ROLE,
  ADS_CAMPAIGNS_DEMO,
} from "../../utils/constants";
import ChatCustomMessage from "../Chat/ChatCustomMessage";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  getIsBlockedActions,
  getNameBusinessTheme,
} from "../../actions/getters";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import { getPathDatabaseByRole } from "../../actions/auth";
import CommonChatMessageInput from "./CommonChatMessageInput";
import { getKeyDatabase } from "../../services/public";
import {
  startGenerateQuickReplies,
  startSendGlobalAgentMessage,
  startSendMessageChatBot,
  startSendMessageOwnerHelpChatAgent,
  startSaveCampaignAgentMessages,
  startDeleteCampaignAgentMessages,
} from "../../actions/chat";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import MinimizeIcon from "@mui/icons-material/Minimize";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ChatWelcomeDescription from "./ChatWelcomeDescription";
import { useCampaignOpeningAssist } from "../../pages/NewAdsCampaign/context/CampaignOpeningAssistContext";
import { useCampaignAgentPanel } from "../../pages/NewAdsCampaign/context/CampaignAgentPanelContext";
import {
  getOpeningAssistEmptyPlaceholderMessageKeys,
  OPENING_ASSIST_EMPTY_PLACEHOLDER,
  OPENING_ASSIST_SYNTHETIC_MESSAGE_ID,
} from "../Chat/openingAssistConstants";
import { resetChatReasoningLoaderProgress } from "../Chat/ChatReasoningIndicator";

import { BOX_SHADOW_CHAT, INPUT_COLOR_BORDER } from "../../utils/colors";
import {
  BUTTON_CAMPAIGN_AGENT_ASK_ALTERNATIVE,
  CAMPAIGN_AGENT_ASK_ALTERNATIVE_MESSAGE,
  CHAT_BOT_DESCRIPTION,
  CHAT_BOT_NAME,
  CHAT_CAMPAIGN_OPEN_ANALYZING_SUGGESTIONS,
  CHAT_THINKING,
  ERROR_OCCURRED,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { Lightbulb } from "lucide-react";
import { startGetAdsCampaignsSchemas } from "../../actions/adsCampaigns";
import { useLocation } from "react-router-dom";
import { getLanguageFormatted, openLink } from "../../utils/string";
import { resolveChatAgentErrorMessage } from "../../utils/errors";
import QuickRepliesChips from "./QuickRepliesChips";
import { formatMessagesForChatAgent } from "./utils/formatMessagesForChatAgent";
import { GLOBAL_REPORT_AGENT_MODE_CHAT } from "../../pages/GlobalReport/utils/globalReportAgentModes";
import {
  CAMPAIGN_AGENT_ACTION_RESULT_MESSAGE_TYPE,
  countPendingOptimizations,
  getFirstPendingOptimizationID,
} from "../../utils/campaignAgentOptimizations";

const SX_CHAT_MESSAGES_FLEX_FILL = { flex: 1, minHeight: 0 };

const SX_CHAT_MESSAGES_ROOT_OVERLAY = {
  ...SX_CHAT_MESSAGES_FLEX_FILL,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const SX_CHAT_MESSAGES_ROOT_LEGACY = {
  flex: 1,
  overflow: "auto",
  height: "100%",
  pt: 0.8,
};

const SX_CHAT_MESSAGES_SCROLL_OVERLAY = {
  ...SX_CHAT_MESSAGES_FLEX_FILL,
  overflow: "auto",
  pt: 0.8,
};

const SX_CHAT_MESSAGES_SCROLL_LEGACY = { display: "contents" };

const SX_CHAT_QUICK_REPLIES_OVERLAY = {
  position: "absolute",
  left: 15,
  right: 15,
  bottom: 0,
  zIndex: 2,
  pointerEvents: "none",
};

const CHAT_LIBRARY_INLINE_STYLE = {
  border: "none",
  backgroundColor: "transparent",
  height: "100%",
  boxShadow: "none",
};

const MESSAGE_LIST_BASE_STYLE = {
  fontFamily: FONT_FAMILY,
  height: "100%",
};

const CommonChatContainer = ({
  showSendFileButton,
  chatConfigurationID,
  chatContext,
  globalReportContext,
  agentFlow,
  context = null,
  placeholderInput,
  showAvatar = false,
  onChangeShowChat = () => {},
  statisticsView,
  onCallbackChangeOptimizations = () => {},
  onRegisterClearPreloadedMessages = () => {},
  embedded = false,
  // hideLauncher = false,
  showOnlyMessages = false,
  showPreviewOptimization = true,
  controlledOpen,
  onChangeOpen,
  exposeSendMessage,
  exposeChatActions,
  exposeMessageCount,
  onAgentReply,
  preloadedMessages = {},
  isLoadingMessages = false,
  hasMoreMessages = false,
  onLoadOlderMessages,
  sx = {},
  contentSx = {},
  showMinimize = true,
  showHeader = true,
  welcomeCenteredSingleColumn = false,
  optimizationsSingleColumn = false,
  demoMode = false,
  demoBotReply,
  disabledInteractions = false,
  messageVariant,
}) => {
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState({});
  const [quickReplies, setQuickReplies] = useState([]);
  const [hasOlderMessages, setHasOlderMessages] = useState(hasMoreMessages);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const skipAutoScrollRef = useRef(false);
  const [isLoadingQuickReplies, setIsLoadingQuickReplies] = useState(false);
  const [open, setOpen] = useState(false);
  const reasoningMessageKeyRef = useRef(null);
  const inFlightSendRef = useRef(false);
  const chatContextLatestRef = useRef(chatContext);
  chatContextLatestRef.current = chatContext;
  const theme = useTheme();

  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();
  const byokPaymentMode = useSelectorApp(
    (state) =>
      state.payments?.byokConfigurationPublic?.defaultPaymentMode || "credits"
  );
  const location = useLocation();
  const LOGO_BUSINESS = theme?.logoChat;

  const isInStatisticsPage = location.pathname === "/ads-campaign-statistics";
  const isDashboardRoute = location.pathname === "/dashboard";
  const isGlobalReportFlow = agentFlow === "globalReport";
  /** Quick replies strip + overlay layout; disabled on dashboard, statistics, and global report. */
  const showQuickRepliesChatOverlay =
    !isDashboardRoute && !isInStatisticsPage && !isGlobalReportFlow;
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const { userID } = dispatch(getPathDatabaseByRole());
  const { t, i18n } = useTranslationApp();
  const { isEnabled: isCampaignAgentPanel, onChangePendingChangesCount } =
    useCampaignAgentPanel();
  const { emptyChatPlaceholder } = useCampaignOpeningAssist();
  const openingAssistPlaceholderMessageKeys =
    getOpeningAssistEmptyPlaceholderMessageKeys(emptyChatPlaceholder);
  const messagesKeys = Object.keys(messages);
  const hasRealMessages = messagesKeys.length > 0;

  const messagesForDisplay = useMemo(() => {
    if (hasRealMessages || !openingAssistPlaceholderMessageKeys) {
      return messages;
    }

    return {
      [OPENING_ASSIST_SYNTHETIC_MESSAGE_ID]: {
        message: "",
        messageType: "system",
        userID: "bot",
        isReasoning: true,
        reasoningMessageKeys: openingAssistPlaceholderMessageKeys,
        creationTime: 0,
      },
    };
  }, [hasRealMessages, openingAssistPlaceholderMessageKeys, messages]);

  const isWaitingForBotResponse =
    Object.values(messages).some((message) => message?.isReasoning) ||
    Boolean(openingAssistPlaceholderMessageKeys && !hasRealMessages);

  const displayMessageKeys = Object.keys(messagesForDisplay);
  const sortedDisplayMessages = displayMessageKeys.sort(
    (a, b) =>
      messagesForDisplay[a].creationTime - messagesForDisplay[b].creationTime
  );
  const displayMessageIndexById = useMemo(
    () => new Map(sortedDisplayMessages.map((id, index) => [id, index])),
    [sortedDisplayMessages]
  );
  const hasAnyMessages = sortedDisplayMessages.length > 0;

  useEffect(() => {
    if (emptyChatPlaceholder === OPENING_ASSIST_EMPTY_PLACEHOLDER.ANALYZING) {
      resetChatReasoningLoaderProgress();
    }
  }, [emptyChatPlaceholder]);

  useEffect(() => {
    if (!isWaitingForBotResponse) {
      resetChatReasoningLoaderProgress();
    }
  }, [isWaitingForBotResponse]);

  const sortedMessages = messagesKeys.sort(
    (a, b) => messages[a].creationTime - messages[b].creationTime
  );
  const pendingOptimizationsCount = isCampaignAgentPanel
    ? countPendingOptimizations({ messages })
    : 0;
  const nextPendingOptimizationID = isCampaignAgentPanel
    ? getFirstPendingOptimizationID({
        messages,
        sortedMessageKeys: sortedMessages,
      })
    : null;

  useEffect(() => {
    if (!isCampaignAgentPanel) {
      return;
    }
    onChangePendingChangesCount(pendingOptimizationsCount);
  }, [
    isCampaignAgentPanel,
    onChangePendingChangesCount,
    pendingOptimizationsCount,
  ]);

  const messageListStyle = showQuickRepliesChatOverlay
    ? MESSAGE_LIST_BASE_STYLE
    : {
        ...MESSAGE_LIST_BASE_STYLE,
        marginTop: 0,
        overflow: "auto",
        background: "transparent",
      };

  const chatMessagesRootSx = showQuickRepliesChatOverlay
    ? SX_CHAT_MESSAGES_ROOT_OVERLAY
    : SX_CHAT_MESSAGES_ROOT_LEGACY;

  const chatMessagesScrollSx = showQuickRepliesChatOverlay
    ? SX_CHAT_MESSAGES_SCROLL_OVERLAY
    : SX_CHAT_MESSAGES_SCROLL_LEGACY;

  const allCurrentChatData = {
    name: `${NAME_BUSINESS} Bot`,
    userID: "bot",
    imageURL: LOGO_BUSINESS,
  };

  // Function to open campaign with chat context
  const openCampaignWithChat = (campaignID) => {
    if (!campaignID) {
      console.error("❌ Cannot open campaign: missing campaignID");
      return null;
    }

    const url = `/ads-campaign?campaignID=${campaignID}&openChat=1`;
    openLink(url);
    return url;
  };

  const persistCampaignID = chatContext?.campaign?.id;
  const shouldPersistCampaignChat =
    embedded &&
    !isGlobalReportFlow &&
    !demoMode &&
    context !== OWNER_ROLE &&
    Boolean(persistCampaignID) &&
    !ADS_CAMPAIGNS_DEMO.includes(persistCampaignID);

  const persistCampaignMessages = (messagesToPersist) => {
    if (!shouldPersistCampaignChat) return;
    dispatch(
      startSaveCampaignAgentMessages({
        campaignID: persistCampaignID,
        messages: messagesToPersist,
      })
    );
  };

  const followUpReplies = useMemo(
    () =>
      CAMPAIGN_AGENT_FOLLOW_UP_REPLIES.map((reply) => ({
        ...reply,
        title: t(reply.title),
        message: t(reply.message),
      })),
    [t]
  );

  const onChangeStatusOptimization = async ({
    optimization,
    messageIndex,
    status,
    applyResult,
  }) => {
    if (disabledInteractions) {
      return;
    }

    if (status === "failed") {
      if (isCampaignAgentPanel) {
        setQuickReplies([
          {
            id: "alternative",
            icon: "warning",
            title: t(BUTTON_CAMPAIGN_AGENT_ASK_ALTERNATIVE),
            message: t(CAMPAIGN_AGENT_ASK_ALTERNATIVE_MESSAGE, {
              title: optimization?.title || "",
            }),
          },
          followUpReplies[0],
        ]);
      }
      return;
    }

    const isDiscarded = status === "discarded";

    if (isInStatisticsPage && !isDiscarded) {
      // The statistics chat is the campaign chat (already persisted), so we only navigate.
      openCampaignWithChat(chatContext?.campaign?.id);
      return;
    }

    const messageKey = sortedMessages[messageIndex];
    if (messageKey && messages[messageKey]) {
      persistCampaignMessages({
        [messageKey]: {
          ...messages[messageKey],
          optimizations: (messages[messageKey].optimizations || []).map(
            (opt) =>
              opt.id === optimization.id
                ? {
                    ...opt,
                    ...(Array.isArray(optimization.changes)
                      ? { changes: optimization.changes }
                      : {}),
                    isApplied: status === "applied",
                    isDiscarded: status === "discarded",
                  }
                : opt
          ),
        },
      });
      setMessages((prevMessages) => {
        const updatedOptimizations = (
          prevMessages[messageKey].optimizations || []
        ).map((opt) =>
          opt.id === optimization.id
            ? {
                ...opt,
                ...(Array.isArray(optimization.changes)
                  ? { changes: optimization.changes }
                  : {}),
                isApplied: status === "applied",
                isDiscarded: status === "discarded",
              }
            : opt
        );

        return {
          ...prevMessages,
          [messageKey]: {
            ...prevMessages[messageKey],
            optimizations: updatedOptimizations,
          },
        };
      });
    }

    if (!embedded) {
      handleClose();
    }

    if (isCampaignAgentPanel && status === "applied" && applyResult) {
      setQuickReplies(followUpReplies);
    }
  };

  const onSendMessage = async (message) => {
    if (disabledInteractions || isTyping || inFlightSendRef.current) {
      return false;
    }

    if (!message || typeof message !== "string" || !message.trim()) {
      return false;
    }

    inFlightSendRef.current = true;
    try {
      if (!demoMode && context !== OWNER_ROLE) {
        const isBlocked = await dispatch(
          getIsBlockedActions({
            action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
            showUsageConfirmationAlert: true,
          })
        );
        if (isBlocked) {
          return false;
        }
      }

      setQuickReplies([]);
      setIsLoadingQuickReplies(false);
      const newMessage = {
        message,
        messageType: "text",
        userID,
        creationTime: new Date().getTime(),
      };
      const key = dispatch(getKeyDatabase());
      persistCampaignMessages({ [key]: newMessage });

      setMessages((prevMessages) => {
        const nextMessages = {
          ...prevMessages,
          [key]: newMessage,
        };

        const reasoningKey = dispatch(getKeyDatabase());
        reasoningMessageKeyRef.current = reasoningKey;
        nextMessages[reasoningKey] = {
          message: "",
          messageType: "system",
          userID: "bot",
          isReasoning: true,
          reasoningMessageKeys: [CHAT_THINKING],
          creationTime: new Date().getTime(),
        };

        generateBotMessage(nextMessages, reasoningKey);
        return nextMessages;
      });

      return true;
    } finally {
      inFlightSendRef.current = false;
    }
  };

  /**
   * @param {string|null} userPrompt Ignored when automaticKind is set: the
   *   server builds that prompt from the kind.
   * @param {{automaticKind?: string}} [options] automaticKind marks a passive
   *   (not user-sent) turn; the backend only honours known kinds.
   */
  const triggerHiddenUserTurn = async (userPrompt, options = {}) => {
    const automaticKind = options?.automaticKind || null;
    if (disabledInteractions || isTyping) {
      return false;
    }

    if (
      !automaticKind &&
      (!userPrompt || typeof userPrompt !== "string" || !userPrompt.trim())
    ) {
      return false;
    }

    setQuickReplies([]);
    setIsLoadingQuickReplies(false);

    const reasoningKey = dispatch(getKeyDatabase());
    reasoningMessageKeyRef.current = reasoningKey;

    setMessages((prevMessages) => {
      const nextMessages = {
        ...prevMessages,
        [reasoningKey]: {
          message: "",
          messageType: "system",
          userID: "bot",
          isReasoning: true,
          reasoningMessageKeys: [CHAT_CAMPAIGN_OPEN_ANALYZING_SUGGESTIONS],
          creationTime: new Date().getTime(),
        },
      };

      generateBotMessage(
        nextMessages,
        reasoningKey,
        automaticKind ? null : userPrompt.trim(),
        automaticKind
      );
      return nextMessages;
    });

    return true;
  };

  const isOpen = typeof controlledOpen === "boolean" ? controlledOpen : open;

  const handleClose = () => {
    if (typeof controlledOpen === "boolean") {
      onChangeOpen && onChangeOpen(false);
    } else {
      setOpen(false);
    }
    onChangeShowChat(false);
  };

  // Welcome cards use i18n keys
  const onWelcomeCardClick = (questionKey) => {
    if (disabledInteractions) {
      return;
    }
    onSendMessage(t(questionKey));
  };

  // Dynamic quick replies already provide the final message
  const onQuickReplyClick = (quickReplyMessage) => {
    if (disabledInteractions) {
      return;
    }
    onSendMessage(quickReplyMessage);
  };

  const generateQuickReplies = async ({
    conversationMessages = [],
    optimizations = [],
  } = {}) => {
    if (
      !Array.isArray(conversationMessages) ||
      conversationMessages.length === 0
    ) {
      return;
    }

    setIsLoadingQuickReplies(true);
    try {
      const response = await dispatch(
        startGenerateQuickReplies({
          messages: conversationMessages,
          optimizations,
          limit: 3,
        })
      );

      if (response?.ok) {
        setQuickReplies(response.data?.replies || []);
        return;
      }

      setQuickReplies([]);
    } finally {
      setIsLoadingQuickReplies(false);
    }
  };

  const generateBotMessage = async (
    messagesSnapshot,
    reasoningKey,
    ephemeralUserTurn = null,
    automaticKind = null
  ) => {
    const messagesFormatted = formatMessagesForChatAgent(
      messagesSnapshot,
      userID,
      ephemeralUserTurn
    );

    if (demoMode) {
      const lastUserMessage = messagesFormatted
        .filter((m) => m.role === "user")
        .pop()?.content;
      const replyText =
        typeof demoBotReply === "function"
          ? demoBotReply(lastUserMessage || ephemeralUserTurn || "")
          : demoBotReply || t(ERROR_OCCURRED);

      setTimeout(() => {
        const newMessageBot = {
          message: replyText,
          learnings: [],
          optimizations: [],
          messageType: "text",
          userID: "bot",
          creationTime: new Date().getTime(),
          completion: JSON.stringify({
            answer: replyText,
            optimizations: [],
            learnings: [],
          }),
        };

        setMessages((prevMessages) => {
          if (reasoningKey && prevMessages[reasoningKey]) {
            return {
              ...prevMessages,
              [reasoningKey]: newMessageBot,
            };
          }
          return prevMessages;
        });
        setIsTyping(false);
      }, 600);
      return;
    }

    // We now show an inline reasoning placeholder instead of typingIndicator
    const isOwnerFlow = context === OWNER_ROLE;

    const chatContextToSend = chatContextLatestRef.current ?? chatContext;
    const sendToAgent = async () => {
      // Owner flow: endpoint devuelve { ok, data: { messages: [...] } }
      if (isOwnerFlow) {
        return await dispatch(
          startSendMessageOwnerHelpChatAgent({ messages: messagesFormatted })
        );
      }

      if (isGlobalReportFlow) {
        return await dispatch(
          startSendGlobalAgentMessage({
            messages: messagesFormatted,
            globalReportContext: {
              ...globalReportContext,
              mode: GLOBAL_REPORT_AGENT_MODE_CHAT,
            },
          })
        );
      }

      if (automaticKind) {
        return await dispatch(
          startSendMessageChatBot({
            chatContext: chatContextToSend,
            automaticRequest: {
              kind: automaticKind,
              language: getLanguageFormatted(i18n.language),
            },
          })
        );
      }

      // Normal flow: endpoint devuelve { ok, data: Array<{answer, optimizations, learnings}> }
      return await dispatch(
        startSendMessageChatBot({
          messages: messagesFormatted,
          chatConfigurationID,
          chatContext: chatContextToSend,
          from: embedded ? MAP_CHAT_FROM.campaign : MAP_CHAT_FROM.report,
        })
      );
    };

    const normalizeAgentResponse = (agentResponse) => {
      if (!agentResponse?.ok) return [];

      if (!isOwnerFlow) {
        return Array.isArray(agentResponse.data) ? agentResponse.data : [];
      }

      const answer = agentResponse.data?.answer ?? [];

      return [
        {
          answer,
          optimizations: [],
          learnings: [],
        },
      ];
    };

    const response = await sendToAgent();

    if (response?.ok) {
      const newMessages = normalizeAgentResponse(response);
      const conversationForQuickReplies = [
        ...messagesFormatted,
        ...newMessages.map((message) => ({
          role: "assistant",
          content: message?.answer || "",
        })),
      ].filter(
        (message) =>
          typeof message?.content === "string" && message.content.trim()
      );
      const optimizationsForQuickReplies = newMessages.flatMap(
        (message) => message?.optimizations || []
      );

      let isFirstReplacementDone = false;
      let firstReplyMessageKey = null;
      for (const [messageIndex, message] of newMessages.entries()) {
        const {
          answer = "",
          optimizations = [],
          learnings = [],
          insights = [],
        } = message;

        onCallbackChangeOptimizations((currentOptimizations) => [
          ...currentOptimizations,
          ...optimizations,
        ]);

        const newMessageBot = {
          message: answer,
          learnings,
          insights,
          optimizations,
          messageType: "text",
          userID: "bot",
          creationTime: new Date().getTime(),
          completion: JSON.stringify({
            answer,
            optimizations,
            learnings,
            insights,
          }),
        };

        const newKey = dispatch(getKeyDatabase());
        persistCampaignMessages({
          [messageIndex === 0 && reasoningKey ? reasoningKey : newKey]:
            newMessageBot,
        });

        setMessages((prevMessages) => {
          if (
            !isFirstReplacementDone &&
            reasoningKey &&
            prevMessages[reasoningKey]
          ) {
            isFirstReplacementDone = true;
            firstReplyMessageKey = reasoningKey;
            return {
              ...prevMessages,
              [reasoningKey]: newMessageBot,
            };
          }
          return {
            ...prevMessages,
            [newKey]: newMessageBot,
          };
        });
      }

      if (firstReplyMessageKey) {
        onAgentReply?.({
          messageKey: firstReplyMessageKey,
          isFirstBotMessageInTurn: true,
        });
      }

      if (firstReplyMessageKey) {
        onAgentReply?.({
          messageKey: firstReplyMessageKey,
          isFirstBotMessageInTurn: true,
        });
      }

      if (!isGlobalReportFlow) {
        await generateQuickReplies({
          conversationMessages: conversationForQuickReplies,
          optimizations: optimizationsForQuickReplies,
        });
      } else {
        setQuickReplies([]);
      }
    } else {
      setQuickReplies([]);
      const errorMessage = resolveChatAgentErrorMessage({
        errorOrResponse: response,
        paymentMode: byokPaymentMode,
        t,
      });
      const errorBotMessage = {
        message: errorMessage,
        learnings: [],
        optimizations: [],
        messageType: "text",
        userID: "bot",
        creationTime: new Date().getTime(),
        completion: JSON.stringify({
          answer: errorMessage,
          optimizations: [],
          learnings: [],
        }),
      };
      const messageKey = reasoningKey || dispatch(getKeyDatabase());
      persistCampaignMessages({ [messageKey]: errorBotMessage });
      setMessages((prevMessages) => ({
        ...prevMessages,
        [messageKey]: errorBotMessage,
      }));
    }
    // Ensure typing indicator remains off (we use inline placeholder)
    setIsTyping(false);
  };

  // Load preloaded messages when initializing the component
  useEffect(() => {
    if (preloadedMessages && Object.keys(preloadedMessages).length > 0) {
      const sanitized = Object.fromEntries(
        Object.entries(preloadedMessages).filter(
          ([, msg]) => !msg?.suppressInChatList
        )
      );
      setMessages(sanitized);

      // Extract optimizations from preloaded messages
      Object.values(sanitized).forEach((message) => {
        if (message.optimizations && message.optimizations.length > 0) {
          onCallbackChangeOptimizations((currentOptimizations) => {
            const newOptimizations = message.optimizations.filter(
              (newOpt) =>
                !currentOptimizations.some((opt) => opt.id === newOpt.id)
            );
            return [...currentOptimizations, ...newOptimizations];
          });
        }
      });
    }
  }, [preloadedMessages, onCallbackChangeOptimizations]);

  useEffect(() => {
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      return;
    }
    const element = window.document.querySelector(".scrollbar-container");
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, [messagesKeys?.length, isTyping]);

  useEffect(() => {
    setHasOlderMessages(hasMoreMessages);
  }, [hasMoreMessages]);

  const handleReachStart = async () => {
    if (
      !onLoadOlderMessages ||
      !hasOlderMessages ||
      isLoadingOlderMessages ||
      isLoadingMessages
    ) {
      return;
    }
    const oldestCreationTime = Object.values(messages).reduce(
      (oldest, message) =>
        message?.creationTime && message.creationTime < oldest
          ? message.creationTime
          : oldest,
      Infinity
    );
    if (!Number.isFinite(oldestCreationTime)) return;

    setIsLoadingOlderMessages(true);
    try {
      const response = await onLoadOlderMessages({
        endBeforeTime: oldestCreationTime,
      });
      if (response?.ok) {
        const olderMessages = Object.fromEntries(
          Object.entries(response.data || {}).filter(
            ([, msg]) => !msg?.suppressInChatList
          )
        );
        // Keep the viewport on the current messages instead of jumping to the bottom.
        skipAutoScrollRef.current = Object.keys(olderMessages).length > 0;
        setMessages((prevMessages) => ({ ...olderMessages, ...prevMessages }));
        setHasOlderMessages(Boolean(response.hasMore));
      } else {
        setHasOlderMessages(false);
      }
    } finally {
      setIsLoadingOlderMessages(false);
    }
  };

  // Exponer handler de envío al padre si se requiere — cuando `disabledInteractions` o `isTyping`
  // cambian, el handler debe reexpuesto o el padre puede quedar con un closure que rechaza todos los sends
  // (SlideChat tras `isSyncingData`).
  useEffect(() => {
    if (typeof exposeSendMessage === "function") {
      exposeSendMessage(onSendMessage);
    }
    if (typeof exposeChatActions === "function") {
      exposeChatActions({
        sendMessage: onSendMessage,
        triggerHiddenUserTurn,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers se reconstruyen cada render; estos flags invalidan cuándo hace falta publicar al padre
  }, [exposeSendMessage, exposeChatActions, disabledInteractions, isTyping]);

  // Exponer conteo de mensajes al padre si se requiere
  useEffect(() => {
    if (typeof exposeMessageCount === "function") {
      const messageCount = Object.keys(messages).length;
      exposeMessageCount(messageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, exposeMessageCount]);

  const clearPreloadedMessages = () => {
    setMessages((prevMessages) => {
      const next = {};
      Object.entries(prevMessages).forEach(([key, message]) => {
        if (!message?.isPreloaded) {
          next[key] = message;
        }
      });
      return next;
    });
  };

  // Register clear function for parent (e.g. when campaign is published/saved)
  useEffect(() => {
    onRegisterClearPreloadedMessages(clearPreloadedMessages);
    return () => onRegisterClearPreloadedMessages(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (demoMode) return;
    dispatch(startGetAdsCampaignsSchemas());
    // eslint-disable-next-line
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        position: sx?.position || "fixed",
        zIndex: 1299,
        width: embedded
          ? { xs: "100%", md: `calc(100% - ${DRAWER_CAMPAIGN}px)` }
          : 500,
        boxShadow: embedded ? BOX_SHADOW_CHAT : 4,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderLeft: embedded ? "none" : `1px solid ${INPUT_COLOR_BORDER}`,
        backgroundColor: "#fff",
        bottom:
          typeof sx?.bottom !== "undefined"
            ? sx.bottom
            : embedded
              ? "88px"
              : statisticsView
                ? 32
                : 100,
        right:
          typeof sx?.right !== "undefined"
            ? sx.right
            : embedded && !statisticsView
              ? "auto"
              : statisticsView
                ? 16
                : 40,
        borderTopLeftRadius: "12px !important",
        borderTopRightRadius: "12px !important",
        borderBottomLeftRadius: embedded ? "0px !important" : "12px !important",
        borderBottomRightRadius: embedded
          ? "0px !important"
          : "12px !important",
        ...sx,
      }}
    >
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            backgroundColor: "#FFF",
            mb: "0.1px", //litte margin to separate the chat from the container
            borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: theme.palette.button.primary.main,
                color: theme.palette.button.primary.contrastText,
              }}
            >
              <Lightbulb className="w-4 h-4 text-white" />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                pl: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ color: "white", fontWeight: 600, lineHeight: 1 }}
              >
                {t(CHAT_BOT_NAME)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "white", opacity: 0.8, lineHeight: 1 }}
              >
                {t(CHAT_BOT_DESCRIPTION)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={async () => {
                setMessages({});
                if (chatContext?.campaign?.id) {
                  const { userID: advertiserID } = dispatch(
                    getPathDatabaseByRole()
                  );
                  if (advertiserID) {
                    await dispatch(
                      startDeleteCampaignAgentMessages({
                        campaignID: chatContext.campaign.id,
                      })
                    );
                  }
                }
              }}
              sx={{ color: "white" }}
              title="Resetear chat"
            >
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
            {showMinimize && (
              <IconButton size="small" onClick={handleClose}>
                <MinimizeIcon sx={{ color: "white", mb: 1.7 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight:
            typeof contentSx?.minHeight !== "undefined"
              ? contentSx.minHeight
              : embedded
                ? "40vh"
                : 350,
          maxHeight:
            typeof contentSx?.maxHeight !== "undefined"
              ? contentSx.maxHeight
              : embedded
                ? "40vh"
                : 500,
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          width: contentSx?.width || "auto",
          height: contentSx?.height || "auto",
        }}
      >
        {!hasAnyMessages ? (
          isLoadingMessages ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 0,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : isGlobalReportFlow ? (
            <Box sx={{ flex: 1, minHeight: 0 }} />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: welcomeCenteredSingleColumn ? "center" : "stretch",
                justifyContent: "center",
                padding: 2,
                textAlign: "center",
                position: "relative",
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              <ChatWelcomeDescription
                campaign={chatContext?.campaign}
                onClickCard={onWelcomeCardClick}
                singleColumn={welcomeCenteredSingleColumn}
                agentContext={chatContext?.agentContext}
              />
            </Box>
          )
        ) : (
          <Box sx={chatMessagesRootSx}>
            <Box sx={chatMessagesScrollSx}>
              <ChatContainerLibrary style={CHAT_LIBRARY_INLINE_STYLE}>
                <MessageList
                  style={messageListStyle}
                  loadingMore={isLoadingOlderMessages}
                  onYReachStart={handleReachStart}
                >
                  {sortedDisplayMessages.map((messageID, visibleIndex) => {
                    const message = messagesForDisplay[messageID];
                    const messageIndexInFullList =
                      displayMessageIndexById.get(messageID) ?? visibleIndex;
                    const isLastMessage =
                      visibleIndex === sortedDisplayMessages.length - 1;
                    const getDirection = () =>
                      message.userID === userID ? "outgoing" : "incoming";
                    const direction = getDirection();
                    const getImageURL = () => allCurrentChatData.imageURL;
                    return (
                      <Message
                        key={messageID}
                        model={{
                          message: message.message,
                          sender: allCurrentChatData.name,
                          direction: direction,
                          type: "html",
                        }}
                        style={{
                          padding: 0,
                          marginBottom:
                            showQuickRepliesChatOverlay &&
                            !isWaitingForBotResponse &&
                            isLastMessage
                              ? theme.spacing(5)
                              : 10,
                          maxWidth:
                            message?.optimizations?.length > 0 ||
                            message?.messageType ===
                              CAMPAIGN_AGENT_ACTION_RESULT_MESSAGE_TYPE
                              ? "100%"
                              : "85%",
                        }}
                      >
                        {direction === "incoming" && showAvatar && (
                          <Avatar
                            src={getImageURL()}
                            name={allCurrentChatData.name}
                          />
                        )}
                        <Message.CustomContent
                          style={{
                            background: "transparent !important",
                          }}
                        >
                          <ChatCustomMessage
                            message={message}
                            messageKey={messageID}
                            direction={direction}
                            embedded={embedded}
                            messageVariant={messageVariant}
                            optimizationsSingleColumn={
                              optimizationsSingleColumn
                            }
                            onChangeStatusOptimization={(params) =>
                              onChangeStatusOptimization({
                                ...params,
                                messageIndex: messageIndexInFullList,
                              })
                            }
                            showPreviewOptimization={showPreviewOptimization}
                            disabledInteractions={disabledInteractions}
                            nextPendingOptimizationID={
                              isLastMessage ? nextPendingOptimizationID : null
                            }
                            onSendAgentMessage={onSendMessage}
                          />
                        </Message.CustomContent>
                      </Message>
                    );
                  })}
                </MessageList>
              </ChatContainerLibrary>
            </Box>
            {showQuickRepliesChatOverlay &&
              !isWaitingForBotResponse &&
              !disabledInteractions &&
              hasAnyMessages && (
                <Box sx={SX_CHAT_QUICK_REPLIES_OVERLAY}>
                  <Box sx={{ pointerEvents: "auto" }}>
                    <QuickRepliesChips
                      onClickChip={onQuickReplyClick}
                      campaign={chatContext?.campaign}
                      agentContext={chatContext?.agentContext}
                      replies={quickReplies}
                      isLoading={isLoadingQuickReplies}
                    />
                  </Box>
                </Box>
              )}
          </Box>
        )}

        {!showOnlyMessages && (
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CommonChatMessageInput
              as="MessageInput"
              disabled={isTyping || disabledInteractions}
              onSendMessage={onSendMessage}
              embedded={embedded}
              showSendFileButton={showSendFileButton}
              placeholderInput={placeholderInput}
              messageVariant={messageVariant}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CommonChatContainer;
